import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/authSlice";
import { api } from "../../lib/api";
import toast from "react-hot-toast";

const EMPTY_FORM = {
  leave_start_date: "",
  leave_end_date: "",
  notes: "",
  handover_type: "auto",
  assigned_tl_user_id: "",
};

function userName(u) {
  if (!u) return "—";
  return `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "—";
}

export default function TlLeaveUpsertPage() {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const isEdit    = Boolean(id);
  const currentUser = useSelector(selectUser);

  const [form, setForm]       = useState(EMPTY_FORM);
  const [allTLs, setAllTLs]   = useState([]);
  const [saving, setSaving]   = useState(false);

  // Load all TLs for manual picker (excluding logged-in user)
  useEffect(() => {
    api.get("/admin/tl-leave-schedules/all-tls")
      .then((res) => {
        const tls = res.data?.data || [];
        setAllTLs(tls.filter((tl) => tl.user_id !== currentUser?.user_id));
      })
      .catch(() => toast.error("Failed to load team leads"));
  }, [currentUser?.user_id]);

  // Load existing record when editing
  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/tl-leave-schedules/${id}`)
      .then((res) => {
        const record = res.data?.data;
        if (!record) { toast.error("Record not found"); navigate("/admin/tl-leave-management"); return; }
        setForm({
          leave_start_date:    record.leave_start_date    || "",
          leave_end_date:      record.leave_end_date      || "",
          notes:               record.notes               || "",
          handover_type:       record.handover_type       || "auto",
          assigned_tl_user_id: record.assigned_tl_user_id || "",
        });
      })
      .catch(() => toast.error("Failed to load leave record"));
  }, [id, isEdit, navigate]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "handover_type" && value === "auto" ? { assigned_tl_user_id: "" } : {}),
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.leave_start_date) return toast.error("Please set a leave start date");
    if (!form.leave_end_date)   return toast.error("Please set a leave end date");
    if (form.leave_end_date < form.leave_start_date) return toast.error("End date cannot be before start date");
    if (form.handover_type === "manual" && !form.assigned_tl_user_id)
      return toast.error("Please select an assigned Team Lead for manual handover");

    const tlId = currentUser?.user_id;
    if (!tlId) return toast.error("Could not identify current user. Please refresh and try again.");

    const payload = {
      tl_user_id:          tlId,
      leave_start_date:    form.leave_start_date,
      leave_end_date:      form.leave_end_date,
      notes:               form.notes || null,
      handover_type:       form.handover_type,
      assigned_tl_user_id: form.handover_type === "manual" ? form.assigned_tl_user_id : null,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/tl-leave-schedules/${id}`, payload);
        toast.success("Leave updated");
      } else {
        await api.post("/admin/tl-leave-schedules", payload);
        toast.success("Leave scheduled");
      }
      navigate("/admin/tl-leave-management");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save leave");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">{isEdit ? "Edit Leave" : "Schedule Leave"}</h1>
        <button
          type="button"
          onClick={() => navigate("/admin/tl-leave-management")}
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
        >
          Back
        </button>
      </div>

      <div className="border rounded-2xl bg-white p-6">
        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Team Lead — read-only */}
          <div>
            <label className="block text-sm font-semibold mb-1">Team Lead</label>
            <div className="w-full rounded-xl border bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <span className="font-medium">{userName(currentUser)}</span>
              {currentUser?.email && (
                <span className="text-gray-400 ml-1">({currentUser.email})</span>
              )}
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Leave Start Date</label>
              <input
                type="date"
                name="leave_start_date"
                value={form.leave_start_date}
                onChange={handleChange}
                className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Leave End Date</label>
              <input
                type="date"
                name="leave_end_date"
                value={form.leave_end_date}
                min={form.leave_start_date || undefined}
                onChange={handleChange}
                className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold mb-1">Notes <span className="font-normal text-gray-400">(optional)</span></label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Reason for leave, special instructions, etc."
              className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black resize-none"
            />
          </div>

          {/* Handover Type */}
          <div>
            <label className="block text-sm font-semibold mb-2">Escalation Handover</label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="handover_type"
                  value="auto"
                  checked={form.handover_type === "auto"}
                  onChange={handleChange}
                  className="accent-gray-900"
                />
                <span>
                  <span className="font-medium">Auto</span>
                  <span className="text-gray-500"> — system picks next available TL</span>
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="handover_type"
                  value="manual"
                  checked={form.handover_type === "manual"}
                  onChange={handleChange}
                  className="accent-gray-900"
                />
                <span>
                  <span className="font-medium">Manual</span>
                  <span className="text-gray-500"> — I assign a specific TL</span>
                </span>
              </label>
            </div>
          </div>

          {/* Manual TL Picker */}
          {form.handover_type === "manual" && (
            <div>
              <label className="block text-sm font-semibold mb-1">Assign To Team Lead</label>
              <select
                name="assigned_tl_user_id"
                value={form.assigned_tl_user_id}
                onChange={handleChange}
                className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
              >
                <option value="">— Select Team Lead —</option>
                {allTLs.map((tl) => (
                  <option key={tl.user_id} value={tl.user_id}>
                    {userName(tl)} ({tl.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate("/admin/tl-leave-management")}
              disabled={saving}
              className="rounded-xl border px-5 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-black text-white px-5 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Schedule Leave"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
